import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useLocation, useParams } from 'react-router';
import { toast } from 'sonner';
import { CreateTextCampaignAutomationRequest } from '../../../extViewmodels/sdk/models/requests/CreateAutomationRequest';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import { getPrincipalInitials } from '../../../models/NameUtils';
import {
	convertRawRichTextContentStateToRichContentEditorState,
	getDisplayNameForBulkEmail,
} from '../../../models/UiUtils';
import {
	invalidateAllAutomationTemplateIdQueries,
	invalidateAutomationTemplateFilter,
	useAutomationTemplateIdQuery,
} from '../../../queries';
import { useTextingCampaignDraftUpdateMutation } from '../../../queries/AutomationTemplate/useTextingCampaignDraftUpdateMutation';
import { useContactFilterQuery } from '../../../queries/Contact/useContactFilterQuery';
import { Button } from '../../components-v2/Button';
import { AutoCompleteSearchField } from '../../components/autocomplete/AutoCompleteSearchField';
import { Avatar2 } from '../../components/Avatar2';
import {
	CampaignRecipientsSearch,
	CampaignRecipientsSearchResult,
} from '../../components/campaigns/CampaignRecipientsSearch';
import { Checkbox } from '../../components/Checkbox';
import { DeprecatedCloseButton } from '../../components/DeprecatedCloseButton';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import { SendFromDropdown } from '../../components/SendFromDropdown';
import { EditItemPenIcon } from '../../components/svgs/icons/EditItemPenIcon';
import { SearchIcon } from '../../components/svgs/icons/SearchIcon';
import { destructive, successGreen } from '../../styles/colors';
import { bs } from '../../styles/styles';
import { sanitizeDraftTitle, validateDraftTitleComprehensive } from '../../utils/validation/draftValidation';
import { CampaignComposerBaseLayout } from '../BulkHwcPages/presentation';
import { useTextingCampaignContacts } from './hooks/useTextingCampaignContacts';
import { PhoneMissingPill } from './PhoneMissingPill';
import { styleSheet } from './styles';
import { TextCampaignPageBody } from './TextCampaignPageBody';

export function TextCampaignViewPage() {
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const errorMessages = useErrorMessages();
	const location = useLocation<{
		initialSendOnBehalf?: Api.ISendOnBehalfPermission;
		initialOwnershipFilter?: Api.IOwnershipFilter;
		initialSendFromUserId?: string;
		contactIds?: string[];
		selectedTags?: Api.AccountTag[];
		selectedContactSearch?: Api.ContactSearch | null;
	}>();
	const { draftId } = useParams<{ draftId: string }>();
	const automationTemplateId = draftId; // Keep the same variable name for consistency
	const automationTemplateIdQuery = useAutomationTemplateIdQuery({
		id: automationTemplateId,
	});

	// Editable title state for drafts
	const [isEditingTitle, setIsEditingTitle] = React.useState(false);
	const [editableTitleValue, setEditableTitleValue] = React.useState('');
	const [titleInputRef] = React.useState(() => React.createRef<HTMLInputElement>());
	const [titleValidationErrors, setTitleValidationErrors] = React.useState<string[]>([]);
	const [editableContent, setEditableContent] = React.useState<Api.IRichContentEditorState | null>(null);
	// Files selected by user but not yet uploaded to server
	const [attachmentFiles, setAttachmentFiles] = React.useState<File[]>([]);
	// Attachments already uploaded and stored on server (from draft)
	const [storedAttachments, setStoredAttachments] = React.useState<Api.IFileAttachment[]>([]);
	/**
	 * Tracks whether attachments have been initialized from the draft.
	 * Prevents re-initialization after user modifications to attachment list.
	 */
	const [hasInitializedAttachments, setHasInitializedAttachments] = React.useState(false);

	// Title update mutation for drafts
	const updateDraftTitleMutation = useTextingCampaignDraftUpdateMutation({
		onSuccess: () => {
			toast.success('Campaign title updated successfully');
			invalidateAllAutomationTemplateIdQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	// Initialize editable title value when template data loads
	React.useEffect(() => {
		if (automationTemplateIdQuery.data?.name && !editableTitleValue) {
			setEditableTitleValue(automationTemplateIdQuery.data.name);
		}
	}, [automationTemplateIdQuery.data?.name, editableTitleValue]);

	// Real-time validation for title editing
	const validateTitleRealTime = React.useCallback(
		async (title: string) => {
			const validation = await validateDraftTitleComprehensive(
				title,
				automationTemplateIdQuery.data?.name,
				automationTemplateId
			);
			setTitleValidationErrors(validation.errors);
			return validation.isValid;
		},
		[automationTemplateIdQuery.data?.name, automationTemplateId]
	);

	// Handle title input change with sanitization and validation
	const handleTitleChange = React.useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const rawValue = event.target.value;
			const sanitizedValue = sanitizeDraftTitle(rawValue);
			setEditableTitleValue(sanitizedValue);

			// Validate in real-time for immediate feedback
			await validateTitleRealTime(sanitizedValue);
		},
		[validateTitleRealTime]
	);

	// Focus input when entering edit mode
	React.useEffect(() => {
		if (isEditingTitle && titleInputRef.current) {
			titleInputRef.current.focus();
			titleInputRef.current.select();
		}
	}, [isEditingTitle, titleInputRef]);

	const [sendFrom, setSendFrom] = React.useState<Api.ISendOnBehalfPermission>(
		location.state?.initialSendOnBehalf ?? userSession.defaultSendOnBehalfPermissions[0]
	);
	const [ownershipFilter, setOwnershipFilter] = React.useState(location.state?.initialOwnershipFilter);
	const [sendFromUserId, setSendFromUserId] = React.useState<string | null>(
		location.state?.initialSendFromUserId ?? null
	);

	// Sidebar state - either recipient selection or recipient management
	const [sidebarMode, setSidebarMode] = React.useState<'selection' | 'contacts'>(() => {
		const hasContactIds = location.state?.contactIds && location.state.contactIds.length > 0;
		const hasTags = location.state?.selectedTags && location.state.selectedTags.length > 0;
		const hasSearch = location.state?.selectedContactSearch?.filter?.criteria;
		return hasContactIds || hasTags || hasSearch ? 'contacts' : 'selection';
	});

	// Track manually removed contact IDs
	const [removedContactIds, setRemovedContactIds] = React.useState<Set<string>>(new Set());

	// Create contact filter request for tag/search-based selections
	const contactFilterRequest = React.useMemo((): Api.IBulkContactsRequest | null => {
		const selectedTags = location.state?.selectedTags || [];
		const selectedContactSearch = location.state?.selectedContactSearch;

		if (selectedTags.length === 0 && !selectedContactSearch?.filter?.criteria) {
			return null; // No filter needed for direct contact selection
		}

		let filterCriteria: Api.IContactFilterCriteria[] = [];

		if (selectedContactSearch?.filter?.criteria) {
			filterCriteria = selectedContactSearch.filter.criteria;
		} else if (selectedTags.length > 0) {
			filterCriteria = [
				{
					op: 'Or',
					criteria: selectedTags.map(tag => ({
						property: Api.ContactFilterCriteriaProperty.Tag,
						value: tag.name,
					})),
				},
			];
		}

		const request = {
			filter: {
				criteria: filterCriteria,
			},
			ownershipFilter: selectedContactSearch?.ownershipFilter ?? ownershipFilter,
		};

		return request;
	}, [location.state?.selectedTags, location.state?.selectedContactSearch, ownershipFilter]);

	// Resolve tag/search-based selections to contact IDs
	const contactFilterQuery = useContactFilterQuery({
		filterRequest: contactFilterRequest!,
		enabled: contactFilterRequest !== null,
		pageSize: 1000, // Get a large batch to capture all matching contacts
	});

	// Combine direct contact IDs with resolved contact IDs from tags/search, excluding manually removed ones
	const allContactIds = React.useMemo(() => {
		const directContactIds = location.state?.contactIds || [];
		const resolvedContactIds =
			contactFilterQuery.data?.pages.flatMap((page: Api.IPagedCollection<Api.IContact>) =>
				page.values.map((contact: Api.IContact) => contact.id!).filter(Boolean)
			) || [];

		// Combine and deduplicate
		const combined = [...directContactIds, ...resolvedContactIds];
		const deduplicated = Array.from(new Set(combined));

		// Exclude manually removed contacts
		const filtered = deduplicated.filter(id => !removedContactIds.has(id));

		return filtered;
	}, [location.state?.contactIds, contactFilterQuery.data, removedContactIds]);

	// Contact management (only used when in contacts mode)
	const { state, dispatch, contactQueries, numberOfRecipients } = useTextingCampaignContacts({
		initialContactIds: allContactIds,
	});

	// Sync sidebar mode with location state changes
	React.useEffect(() => {
		const hasContactIds = location.state?.contactIds && location.state.contactIds.length > 0;
		const hasTags = location.state?.selectedTags && location.state.selectedTags.length > 0;
		const hasSearch = location.state?.selectedContactSearch?.filter?.criteria;
		const hasRecipients = hasContactIds || hasTags || hasSearch;

		if (hasRecipients && sidebarMode === 'selection') {
			setSidebarMode('contacts');
		} else if (!hasRecipients && sidebarMode === 'contacts') {
			setSidebarMode('selection');
		}
	}, [location.state?.contactIds, location.state?.selectedTags, location.state?.selectedContactSearch, sidebarMode]);

	// Sync hook state with resolved contact IDs
	React.useEffect(() => {
		const currentContactIds = state.contactIds;

		// If resolved contact IDs are different from current state, sync them
		if (
			allContactIds.length !== currentContactIds.length ||
			!allContactIds.every(id => currentContactIds.includes(id))
		) {
			// Clear current contacts
			currentContactIds.forEach(id => {
				dispatch({ type: 'removeRecipient', value: id });
			});

			// Add new contacts
			allContactIds.forEach(id => {
				dispatch({ type: 'addRecipient', value: id });
			});
		}
	}, [allContactIds, state.contactIds, dispatch]);

	// Get initial content from the appropriate source (draft or published)
	const initialCampaignContent = React.useMemo(() => {
		if (!automationTemplateIdQuery.data) return null;

		const steps = automationTemplateIdQuery.data.draft?.steps;

		return steps?.[0]?.content ? convertRawRichTextContentStateToRichContentEditorState(steps[0].content) : null;
	}, [automationTemplateIdQuery.data]);

	// Get initial attachments from the appropriate source
	const initialAttachments = React.useMemo(() => {
		if (!automationTemplateIdQuery.data) return [];

		const steps = automationTemplateIdQuery.data.draft?.steps;

		return (steps?.[0] as Api.TextingAutomationStep)?.attachments || [];
	}, [automationTemplateIdQuery.data]);

	// Initialize editable state when template loads
	React.useEffect(() => {
		if (initialCampaignContent && !editableContent) {
			setEditableContent(initialCampaignContent);
		}
		// Initialize attachments only once from draft to prevent overwriting user changes
		if (initialAttachments.length > 0 && !hasInitializedAttachments) {
			setStoredAttachments(initialAttachments);
			setHasInitializedAttachments(true);
		}
	}, [initialCampaignContent, initialAttachments, editableContent, hasInitializedAttachments]);

	const onSendOnBehalf = (option: Api.ISendOnBehalfPermission, user?: Api.IUser) => {
		setSendFrom(option);
		setSendFromUserId(user?.id);
		setOwnershipFilter(option?.requiredFilter);
	};

	// Handle recipient selection from the search component
	const handleNextClick = (value: CampaignRecipientsSearchResult) => {
		// Extract contact IDs from different sources, similar to template search logic
		const initialContactIdsToInclude = value.selectedContacts.map(c => c.id!).filter(Boolean);

		// For simple contact selection (no tags or search criteria), directly use contacts
		if (
			initialContactIdsToInclude.length > 0 &&
			!value.selectedContactSearch?.filter?.criteria &&
			value.selectedTags.length === 0
		) {
			// Clear removed contacts when selecting new recipients
			setRemovedContactIds(new Set());

			// Update the location state with contact IDs
			fullscreenModal.history.replace(location.pathname, {
				...location.state,
				contactIds: initialContactIdsToInclude,
			});
			return;
		}

		// Clear removed contacts when selecting new recipients
		setRemovedContactIds(new Set());

		const selectedCriteria = value.selectedContactSearch;
		let initialContactFilterCriteriaList: Api.IContactFilterCriteria[];
		if (selectedCriteria?.filter?.criteria) {
			initialContactFilterCriteriaList = selectedCriteria.filter.criteria;
		} else if (value.selectedTags.length > 0) {
			initialContactFilterCriteriaList = [
				{
					op: 'Or',
					criteria: value.selectedTags.map(tag => ({
						property: Api.ContactFilterCriteriaProperty.Tag,
						value: tag.name,
					})),
				},
			];
		}
		fullscreenModal.history.push(`/texting-campaign/draft/filter/${draftId}`, {
			initialContactIdsToInclude,
			initialFilterCriteriaList: initialContactFilterCriteriaList,
			initialSendOnBehalf: sendFrom,
			initialSendFromUserId: sendFromUserId,
			initialOwnershipFilter: selectedCriteria?.ownershipFilter ?? ownershipFilter,
			initialCampaignTitle: automationTemplateIdQuery.data?.name,
			initialContentEditorState: editableContent,
			initialAttachmentFiles: attachmentFiles,
			initialStoredAttachments: storedAttachments,
		});
	};

	// Handle going back to recipient selection
	const handleBackToSelection = () => {
		setSidebarMode('selection');
		// Clear removed contacts when going back to selection
		setRemovedContactIds(new Set());
		// Clear contact IDs from state
		fullscreenModal.history.replace(location.pathname, {
			...location.state,
			contactIds: undefined,
			selectedTags: undefined,
			selectedContactSearch: undefined,
		});
	};

	// Handle proceeding to schedule send
	const onNextButtonClick = () => {
		fullscreenModal.history.push(`/texting-campaign/schedule-send/${automationTemplateId}`, {
			request: {
				includeContactIds: state.contactIds,
				sendFromOptions: {
					mode: sendFrom?.sender?.mode,
					selectedUser: sendFromUserId ?? undefined,
					connectionType: sendFrom?.sender?.connectionType,
				},
				autoStart: false,
				putOnHold: false,
				skipIfInProgress: false,
				skipIfInPrevious: false,
				groupByHousehold: false,
			},
		});
	};

	const handleRemoveRecipient = (contact: Api.IContact) => {
		if (state.contactIds.length === 1) {
			handleBackToSelection();
			return;
		}

		// Add the contact ID to the removed contacts set
		setRemovedContactIds(prev => new Set([...prev, contact.id!]));

		// Also dispatch to hook for immediate UI update
		dispatch({ type: 'removeRecipient', value: contact.id! });
	};

	const renderRecipientItem = (contact: Api.IContact) => {
		let companyLine = '';
		if (contact.companyName != null) {
			companyLine = contact.jobTitle != null ? `${contact.jobTitle} at ${contact.companyName}` : contact.companyName;
		}
		const nameLabel = getDisplayNameForBulkEmail(contact);
		return (
			<div
				className={css(
					bs.flex,
					bs.itemsCenter,
					bs.border0,
					bs.borderB,
					bs.borderSolid,
					bs.borderGray200,
					bs.cursorPointer,
					bs.py5,
					bs.px0,
					bs.relative
				)}
				key={contact.id}
			>
				<DeprecatedCloseButton
					onClick={ev => {
						ev.stopPropagation();
						handleRemoveRecipient(contact);
					}}
				/>
				<Avatar2
					styleDeclaration={styleSheet.recipientsListItemAvatar}
					imgSrc={contact.profilePic}
					fallbackText={getPrincipalInitials(contact)}
				/>
				<div className={css(bs.flex1, bs.textSm, bs.ml4, bs.overflowHidden)}>
					<div className={css(styleSheet.recipientsListItemDetailsName)}>{nameLabel}</div>
					<div className={css(styleSheet.recipientsListItemDetailsCompanyName, bs.truncateText)}>{companyLine}</div>
				</div>
				{!contact.canReceiveText ? <PhoneMissingPill styleDeclarations={[bs.absolute, bs.bottom2, bs.right2]} /> : null}
			</div>
		);
	};

	// Handle title editing
	const handleStartEditTitle = () => {
		setIsEditingTitle(true);
	};

	const handleSaveTitle = async () => {
		if (!automationTemplateIdQuery.data) {
			return;
		}

		// Comprehensive validation before saving
		const validation = await validateDraftTitleComprehensive(
			editableTitleValue,
			automationTemplateIdQuery.data.name,
			automationTemplateId
		);

		if (!validation.isValid) {
			setTitleValidationErrors(validation.errors);
			// Show first error as toast for immediate user feedback
			errorMessages.push({ messages: [validation.errors[0]] });
			return;
		}

		// Don't save if title hasn't changed
		if (editableTitleValue.trim() === automationTemplateIdQuery.data.name) {
			setIsEditingTitle(false);
			setTitleValidationErrors([]);
			return;
		}

		// Get current draft content to preserve it during title update
		const currentDraftStep = automationTemplateIdQuery.data.draft?.steps?.[0];
		if (!currentDraftStep) {
			errorMessages.push({
				messages: ['Cannot update title: draft content not found'],
				title: 'Error',
			});
			return;
		}

		const request: CreateTextCampaignAutomationRequest = {
			campaignName: editableTitleValue.trim(),
			content: currentDraftStep.content,
			autoStart: false,
			putOnHold: false,
			skipIfInProgress: false,
			skipIfInPrevious: false,
			sendFromOptions: { mode: 'CurrentUser' as const },
			includeContactIds: [] as string[],
			excludeContactIds: [] as string[],
			attachments: (currentDraftStep as Api.TextingAutomationStep)?.attachments || [],
			groupByHousehold: false,
		};

		try {
			await updateDraftTitleMutation.mutateAsync({
				templateId: automationTemplateId,
				request,
			});
			setIsEditingTitle(false);
		} catch {
			// Error handling is done in the mutation's onError callback
		}
	};

	const handleCancelEditTitle = React.useCallback(() => {
		setIsEditingTitle(false);
		setTitleValidationErrors([]);
		// Reset to original value
		if (automationTemplateIdQuery.data?.name) {
			setEditableTitleValue(automationTemplateIdQuery.data.name);
		}
	}, [automationTemplateIdQuery.data?.name]);

	const handleTitleKeyPress = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			handleSaveTitle();
		} else if (event.key === 'Escape') {
			event.preventDefault();
			handleCancelEditTitle();
		}
	};

	// Save draft mutation
	const updateDraftMutation = useTextingCampaignDraftUpdateMutation({
		onSuccess: () => {
			// Invalidate the drafts query so updated drafts reflect immediately
			invalidateAutomationTemplateFilter();
			invalidateAllAutomationTemplateIdQueries();
			// Show success toast
			toast.success('Draft saved successfully');
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});
	const handleSaveDraft = () => {
		if (!automationTemplateIdQuery.data || !editableContent) return;

		const request: CreateTextCampaignAutomationRequest = {
			campaignName: automationTemplateIdQuery.data.name || '',
			content: editableContent.getRawRichTextContent(),
			autoStart: false,
			putOnHold: false,
			skipIfInProgress: false,
			skipIfInPrevious: false,
			sendFromOptions: sendFrom?.sender,
			includeContactIds: [],
			excludeContactIds: [],
			attachments: storedAttachments,
			groupByHousehold: false,
		};

		updateDraftMutation.mutate({
			templateId: automationTemplateId,
			request,
			files: attachmentFiles,
		});
	};

	return (
		<>
			<MultiContainerHeader
				fullscreenHeader='Edit Texting Campaign'
				onFullscreenRequestBack={ev => {
					ev.preventDefault();
					fullscreenModal.dismissModal();
				}}
				onFullscreenRequestClose={() => fullscreenModal.dismissModal()}
			/>
			<CampaignComposerBaseLayout
				mainHeader={
					<div className={css(bs.px3, bs.py5, bs.flex, bs.gap2, bs.flexCol)}>
						<div className={css(bs.mb2)}>
							{automationTemplateIdQuery.status === 'success' ? (
								<div className={css(bs.flex, bs.itemsCenter, bs.gap2)}>
									{isEditingTitle ? (
										<div className={css(bs.flex, bs.itemsCenter, bs.gap2, bs.flex1)}>
											<div className={css(bs.flex, bs.flexCol, bs.flex1)}>
												<input
													ref={titleInputRef}
													type='text'
													value={editableTitleValue}
													onChange={handleTitleChange}
													onKeyDown={handleTitleKeyPress}
													className={css(
														bs.flex1,
														bs.textLg,
														bs.fontBold,
														bs.border,
														bs.borderSolid,
														bs.rounded,
														bs.px2,
														bs.py1,
														bs.bgWhite
													)}
													style={{
														borderColor: titleValidationErrors.length > 0 ? destructive : undefined,
													}}
													placeholder='Campaign title'
													aria-invalid={titleValidationErrors.length > 0}
													aria-describedby={titleValidationErrors.length > 0 ? 'title-validation-errors' : undefined}
												/>
												{titleValidationErrors.length > 0 && (
													<div
														id='title-validation-errors'
														className={css(bs.mt1, bs.textXs)}
														style={{ color: destructive }}
														role='alert'
													>
														{titleValidationErrors[0]}
													</div>
												)}
											</div>
											<Button
												variant='primary'
												size='sm'
												onClick={handleSaveTitle}
												disabled={updateDraftTitleMutation.isPending || !editableTitleValue.trim()}
												isLoading={updateDraftTitleMutation.isPending}
											>
												Save
											</Button>
											<Button
												variant='link'
												size='sm'
												onClick={handleCancelEditTitle}
												disabled={updateDraftTitleMutation.isPending}
											>
												Cancel
											</Button>
										</div>
									) : (
										<div className={css(bs.flex, bs.itemsCenter, bs.gap2, bs.flex1)}>
											<h2 className={css(bs.m0)}>{automationTemplateIdQuery.data?.name}</h2>
											<Button variant='link' size='icon' onClick={handleStartEditTitle} styles={[bs.p1]}>
												<EditItemPenIcon fillColor={successGreen} />
											</Button>
										</div>
									)}
								</div>
							) : (
								<LoadingSpinner type='small' />
							)}
						</div>
						<SendFromDropdown
							disabled={!userSession.canSendOnBehalf}
							sendFromOption={sendFrom?.sender}
							canSendOnBehalf={userSession.canSendOnBehalf}
							allowedContactBasedOptions='all'
							sendOnBehalfOf={onSendOnBehalf}
						/>
					</div>
				}
				sideBar={
					sidebarMode === 'selection' ? (
						<CampaignRecipientsSearch onSubmit={handleNextClick} />
					) : (
						<>
							<div className={css(styleSheet.recipientsHeader)}>
								<div className={css(styleSheet.recipientsHeaderTitle)}>
									<div>Recipients</div>
								</div>
								<div className={css(bs.flex, bs.itemsCenter, bs.flexWrap, bs.textSm, bs.justifyStart)}>
									<div className={css(bs.flex, bs.mr2)}>
										{numberOfRecipients != null
											? `${numberOfRecipients} out of ${state.contactIds.length} contact${
													state.contactIds.length !== 1 ? 's' : ''
												} with phone numbers`
											: null}
									</div>
								</div>
								<AutoCompleteSearchField
									anchorClassName='email-recipients-list-autocomplete-search-field-anchor'
									className='email-recipients-list-autocomplete-search-field'
									clearSearchFieldAfterSelectingItem={true}
									dropdownContentClassName={css(bs.wFull)}
									inputId='email-recipients-list-search-input'
									inputProps={{
										placeholder: 'Add additional contacts',
									}}
									leftAccessory={<SearchIcon className='email-recipients-list-autocomplete-search-field-icon' />}
									onItemSelected={(contact: Api.IContact) => {
										if (contact.id) {
											dispatch({ type: 'addRecipient', value: contact.id });
										}
									}}
									resultsLimit={6}
									type={Api.ResourceAutoCompleteViewModelType.Contact}
								/>
							</div>
							<div className={css(bs.flex, bs.flex1, bs.flexCol, bs.overflowXHidden, bs.overflowYAuto)}>
								<div>
									{contactQueries.map((contactQuery, i) => {
										if (!contactQuery.data) {
											return <LoadingSpinner key={`${contactQuery.data?.id}-${i}`} type='small' />;
										}
										return renderRecipientItem(contactQuery.data);
									})}
								</div>
							</div>
							<div className={css(bs.py2, bs.px4, bs.border0, bs.borderT, bs.borderSolid, bs.borderGray200)}>
								<Checkbox
									id='textingCampaignAgreement-checkbox'
									className={css(bs.mb6)}
									checked={state.consentChecked}
									onChange={() => dispatch({ type: 'toggleConsentChecked' })}
								>
									<span className={css(bs.ml2)}>
										I agree that the recipients have opted in to receive texts and this is not a marketing campaign.
									</span>
								</Checkbox>
								<Button
									type='button'
									styles={[bs.wFull]}
									onClick={onNextButtonClick}
									disabled={!numberOfRecipients || !state.consentChecked}
								>
									<span>Next: Send Options</span>
								</Button>
							</div>
						</>
					)
				}
			>
				<>
					<div
						className={css(
							bs.flex,
							bs.itemsCenter,
							bs.justifyEnd,
							bs.py3,
							bs.px5,
							bs.gap2,
							bs.border0,
							bs.borderB,
							bs.borderSolid,
							bs.borderMentionDark
						)}
					>
						<Button
							variant='secondary'
							disabled={updateDraftMutation.isPending}
							onClick={handleSaveDraft}
							isLoading={updateDraftMutation.isPending}
						>
							Save Draft
						</Button>
					</div>
					<div
						className={css(
							bs.itemsCenter,
							bs.boxBorder,
							bs.flex,
							bs.flex1,
							bs.flexCol,
							bs.overflowAuto,
							bs.py8,
							bs.px6,
							bs.bgGray50
						)}
					>
						<div className={css(bs.flex, bs.flexCol, bs.maxWXl, bs.wFull, bs.mxAuto)}>
							{automationTemplateIdQuery.status === 'success' ? (
								<TextCampaignPageBody
									contentEditorState={editableContent}
									attachmentFiles={attachmentFiles}
									storedAttachments={storedAttachments}
									onAttachFiles={files => setAttachmentFiles(prev => [...prev, ...files])}
									onRemoveAttachmentFile={(fileToRemove: File) => {
										setAttachmentFiles(prev => prev.filter(file => file !== fileToRemove));
									}}
									onRemoveStoredAttachment={(attachmentId: string) => {
										setStoredAttachments(prev => prev.filter(attachment => attachment.id !== attachmentId));
									}}
									onContentStateChanged={setEditableContent}
								/>
							) : (
								<LoadingSpinner type='small' />
							)}
						</div>
					</div>
				</>
			</CampaignComposerBaseLayout>
		</>
	);
}
